//map.js
// Завантаження Google Maps API динамічно
import { loadGoogleMaps } from './loadGoogleMaps.js';
// Функція, яка налаштовує інпут-поля адрес (Autocomplete, події)
import { setupInputs } from '../components/tripPlanning/step1/trip-form.js';
// Функція, яка налаштовує логіку маршрутів (побудова, відстань, час)
import { initRouting } from './route.js';
// Відновлює маркери, які були збережені в localStorage
import { restoreMarkersFromLS } from '../components/tripPlanning/step1/trip-form.js';

import { customMapStyle } from './map_settings.js';

// --- Налаштування зони аеропорту (варіант А: коло) ---
const PRG_CENTER = { lat: 50.1012, lng: 14.2697 }; // орієнтовний центр T1/T2
const PRG_RADIUS_M = 1400; // радіус з запасом (1.4 км)

function haversineMeters(a, b) {
  const toRad = d => (d * Math.PI) / 180;
  const R = 6371000; // м
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function isInPragueAirport(point) {
  return haversineMeters(point, PRG_CENTER) <= PRG_RADIUS_M;
}

// Головна функція — викликається з main.js
export async function initMap() {
  // 1. Завантажуємо скрипт Google Maps API
  const lang =
    document.documentElement.lang ||
    (navigator.language && navigator.language.split('-')[0]) ||
    'cs';

  await loadGoogleMaps({ lang });

  // 2. Імпортуємо потрібні частини Google Maps API (це асинхронно)
  const [
    { Map }, // Для створення самої карти
    { Marker }, // Для маркерів A і B
    { Autocomplete }, // Для підказок у полях вводу
    { DirectionsService, DirectionsRenderer, DistanceMatrixService }, // Для маршрутів
  ] = await Promise.all([
    google.maps.importLibrary('maps'),
    google.maps.importLibrary('marker'),
    google.maps.importLibrary('places'),
    google.maps.importLibrary('routes'),
  ]);

  // 3. Центр карти — Прага
  const defaultLocation = { lat: 50.0755, lng: 14.4378 };

  // 4. Створюємо саму карту та додаємо її в елемент з id="map"
  const mapEl = document.getElementById('map');
  const map = new Map(mapEl, {
    center: defaultLocation,
    zoom: 15,
    styles: customMapStyle,
    zoomControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,

    scrollwheel: false,
  });

  // Зміщує перший вьюпорт так, щоб центр карти був у центрі “червоного квадратика”
  function alignInitialViewportToBox() {
    const pad = getRouteViewportPadding();
    // різниця падінгів перетворюється на зсув у пікселях
    const dx = Math.round((pad.left - pad.right) / 2);
    const dy = Math.round((pad.top - pad.bottom) / 2);
    // фіксуємо бажаний стартовий масштаб, зсуваємо вьюпорт
    // map.setCenter(defaultLocation);
    map.setZoom(12);
    map.panBy(-dx, -dy);
  }

  // Виконуємо один раз після першого рендера карти
  google.maps.event.addListenerOnce(map, 'idle', alignInitialViewportToBox);

  // Падінги для "червоного квадратика" (права частина карти, без лівої панелі)
  function getRouteViewportPadding() {
    const mapRect = mapEl.getBoundingClientRect();

    // 1) Якщо створите невидимий прямокутник-ціль (позиціонуйте його там, де має бути маршрут):
    // <div class="route-viewport-box" style="position:absolute; inset: 16px 16px 160px calc(320px + 16px); pointer-events:none;"></div>
    const box = document.querySelector('.route-viewport-box');
    if (box) {
      const r = box.getBoundingClientRect();
      return {
        top: Math.max(0, r.top - mapRect.top),
        left: Math.max(0, r.left - mapRect.left),
        right: Math.max(0, mapRect.right - r.right),
        bottom: Math.max(0, mapRect.bottom - r.bottom),
      };
    }

    // 2) Або рахуємо відступи від лівої панелі .map-form
    const panel = document.querySelector('.map-form');
    const panelRect = panel ? panel.getBoundingClientRect() : null;
    const left = panelRect
      ? Math.max(0, Math.round(panelRect.right - mapRect.left)) + 16
      : 16;

    // Можна врахувати нижню картку з підсумком маршруту, якщо вона перекриває карту
    const routeInfo =
      document.querySelector('[data-route-summary]') ||
      document.querySelector('.route-summary');
    const routeInfoRect = routeInfo ? routeInfo.getBoundingClientRect() : null;
    const bottom = routeInfoRect
      ? Math.max(24, Math.round(mapRect.bottom - routeInfoRect.top) + 8)
      : 24;

    return { top: 16, right: 16, left, bottom };
  }

  // Вписуємо bounds маршруту у “квадратик” (через padding)
  function fitRouteBounds(bounds) {
    console.log('bounds', bounds);
    if (!bounds) return;
    try {
      map.fitBounds(bounds, getRouteViewportPadding());
    } catch (e) {
      console.warn('fitRouteBounds failed', e);
    }
  }

  // --- Малюємо коло зони аеропорту на мапі ---
  const airportCircle = new google.maps.Circle({
    strokeColor: '#2563eb',
    strokeOpacity: 0.8,
    strokeWeight: 1,
    fillColor: '#2563eb',
    fillOpacity: 0.08,
    map,
    center: PRG_CENTER,
    radius: PRG_RADIUS_M,
    clickable: false,
  });

  // 5. Формуємо об'єкт із усіма потрібними класами та картою
  const context = {
    map,
    Marker,
    Autocomplete,
    DirectionsService,
    DirectionsRenderer,
    DistanceMatrixService,
    fitRouteBounds,

    // Додаємо утиліти зони аеропорту в контекст
    airportZone: {
      center: PRG_CENTER,
      radiusMeters: PRG_RADIUS_M,
      circle: airportCircle,
      isInZone: isInPragueAirport,
    },
  };

  // 6. Ініціалізуємо інпути адрес (ввід, автозаповнення, клацання по карті)
  setupInputs(context);
  // 7. Ініціалізуємо сервіси маршрутів та обчислення відстані
  initRouting(context);
  // 8. Відновлюємо маркери та дані з LocalStorage, якщо користувач щось вводив раніше
  restoreMarkersFromLS();
}
